// Master toggle for the Vault tier — flip to `true` to activate gating
export const PRO_TIER_ENABLED = true;

// ---------------------------------------------------------------------------
// Tier types
// ---------------------------------------------------------------------------

export type Tier = "trial" | "vault" | "unlimited" | "expired";

export type RequiredAccess = "light" | "heavy" | "asset";

// ---------------------------------------------------------------------------
// Tier limits (client-side reference; server enforces authoritatively)
// ---------------------------------------------------------------------------

export const TIER_LIMITS = {
  trial:     { assets: Infinity,       ai_actions_per_month: Infinity },
  vault:     { assets: Infinity, ai_actions_per_month: 15 },
  unlimited: { assets: Infinity, ai_actions_per_month: Infinity },
  expired:   { assets: 0,        ai_actions_per_month: 0 },
} as const;

// ---------------------------------------------------------------------------
// Pricing display constants (updated for new model)
// ---------------------------------------------------------------------------

export const VAULT_PRICE_MONTHLY = "$1.99/mo";
export const VAULT_PRICE_ANNUAL = "$11.99/yr";
export const UNLIMITED_PRICE_ANNUAL = "$39/yr";
export const BOOST_PRICE = "$3.99";
export const PRO_PRICE = VAULT_PRICE_ANNUAL; // default display price (backward compat)

// ---------------------------------------------------------------------------
// Legacy FREE_TIER_LIMITS
//
// Under the new pricing model there is NO permanent free tier — only a 7-day
// trial (+ 30-day grace for pre-launch accounts). These constants are still
// referenced by UI copy for the trial / grace asset cap and by the Upgrade
// page scan-limit reason banner. `ai_scans_per_month` is kept at 0 because
// expired users get no AI actions; trial/grace users get unlimited.
// ---------------------------------------------------------------------------

export const FREE_TIER_LIMITS = {
  assets: 25,
  ai_scans_per_month: 0,
  email_ingests_per_month: 0,
};

// ---------------------------------------------------------------------------
// Feature registry (kept for backward compat with ProGate featureKey)
// ---------------------------------------------------------------------------

export interface VaultFeature {
  label: string;
  description: string;
}

/**
 * Registry of paid / Vault-only features.
 *
 * Important entitlement semantics:
 * - Vault-only access: add the feature here and gate it with <ProGate> / <ProGateRoute>
 * - Trial-or-paid AI access: do NOT add it here; use explicit app logic
 * - Universal/free access: do NOT add it here; render it directly
 *
 * Example of universal access: recalls are available to every user and must never
 * be represented as a Vault-gated feature.
 */
export const PRO_FEATURES = {
  resale_value: {
    label: "Resale Value",
    description: "AI-powered market valuation for your items",
  },
  support_warranty: {
    label: "Support & Warranty",
    description: "Find official support pages and warranty info",
  },
  unlimited_assets: {
    label: "Unlimited Assets",
    description: "Track more than 25 items in your inventory",
  },
  unlimited_scans: {
    label: "Unlimited AI Scans",
    description: "No monthly cap on AI-powered photo analysis",
  },
  unlimited_email: {
    label: "Unlimited Email Ingest",
    description: "Forward as many receipts as you want",
  },
  insurance_report: {
    label: "Insurance Reports",
    description: "Generate detailed PDF reports for insurance claims",
  },
  bulk_capture: {
    label: "Bulk Capture",
    description: "Scan and add multiple items at once",
  },
  generate_listing: {
    label: "Listing Generator",
    description: "AI-generated resale listings for your items",
  },
} as const satisfies Record<string, VaultFeature>;

export type VaultFeatureKey = keyof typeof PRO_FEATURES;

export function isVaultFeature(key: string): key is VaultFeatureKey {
  return key in PRO_FEATURES;
}
